import React, { useEffect, useState } from "react";

import type { GridPromoSnippet } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Themed } from "../../styles/themes";
import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock/SpacerBlock";
import { useOverflow } from "../TwoUpCardsBlock/TwoUpCard";

import categoryStyles from "../CategoryContentBlock/index.module.scss";
import styles from "./index.module.scss";

type Content = GridPromoSnippet["body"][number]["value"];
type Item = NonNullable<Content["items"]>[number];
type Alignment = Content["alignment"];

const alignmentVars: Record<NonNullable<Alignment>, React.CSSProperties> = {
    top_left: {
        "--align-main": "flex-start",
        "--align-cross": "flex-start",
        "--text-align": "left",
    },
    center_center: {
        "--align-main": "center",
        "--align-cross": "center",
        "--text-align": "center",
    },
};

const RenderedItem = ({ item }: { item: Item }) => {
    if (item.type === "rich_text_stream") {
        return <RichTextStreamBlock value={item.value} />;
    }
    if (item.type === "image") {
        if (!item.value.image) return null;
        const style: React.CSSProperties = {
            "--image-width": `${item.value.width ?? 100}%`,
        };
        if (item.value.max_width_px) {
            style["--image-max-width"] = `${item.value.max_width_px}px`;
        }
        return (
            <ImageChooserBlock
                value={item.value.image}
                attrs={{ className: styles.imageBlock, style }}
            />
        );
    }
    if (item.type === "spacer") {
        return <SpacerBlock value={item.value} />;
    }
    return null;
};

const applyOverflowFallbacks = (
    items: Item[],
    hideBody: boolean,
    smallerHeading: boolean,
): Item[] => {
    const filtered: Item[] = [];
    for (const item of items) {
        if (hideBody && item.type === "image") continue;
        // When overflowing, collapse runs of spacers — typically created when
        // an image between two spacers is removed. Two authored spacers in a
        // row are rare and reduce to one here as a side effect.
        const last = filtered[filtered.length - 1];
        if (hideBody && item.type === "spacer" && last?.type === "spacer") {
            continue;
        }
        filtered.push(item);
    }
    return filtered.map((item) => {
        if (item.type !== "rich_text_stream") return item;
        const { content, size_adjustment, ...rest } = item.value;
        return {
            ...item,
            value: {
                ...rest,
                size_adjustment: smallerHeading ? "-1" : size_adjustment,
                content: hideBody
                    ? (content?.filter((block) => block.type !== "body") ??
                      content)
                    : content,
            },
        };
    });
};

const AdaptiveContent = ({ items }: { items: Item[] }) => {
    const [ratio, setRatio] = useState(1);
    const measureRef = useOverflow(setRatio);

    const hideBodyAndImage = ratio > 1;
    const smallerHeading = ratio > 1.25;
    const clampHeading = ratio > 1.4;

    useEffect(() => {
        const headingIds = items.flatMap((item) =>
            item.type === "rich_text_stream"
                ? (item.value.content ?? [])
                      .filter((block) => block.type === "heading")
                      .map((block) => block.id)
                : [],
        );
        headingIds.forEach((id) => {
            const element = document.getElementById(id);
            element?.classList.toggle(styles.clampedHeading, clampHeading);
        });
    }, [clampHeading, items]);

    const displayItems = applyOverflowFallbacks(
        items,
        hideBodyAndImage,
        smallerHeading,
    );

    return (
        <>
            <div className={styles.content}>
                {displayItems.map((item) => (
                    <RenderedItem key={item.id} item={item} />
                ))}
            </div>
            <div ref={measureRef} className={styles.measure} aria-hidden="true">
                {items.map((item) => (
                    <RenderedItem key={`measure-${item.id}`} item={item} />
                ))}
            </div>
        </>
    );
};

interface Props {
    value: GridPromoSnippet;
}

export const GridPromoCard = ({ value }: Props) => {
    const content = value.body[0]?.value;
    if (!content) return null;

    const items = content.items ?? [];
    const media = content.media;
    const hasItems = items.length > 0;
    const alignStyle = content.alignment
        ? alignmentVars[content.alignment]
        : undefined;

    if (media && media.length > 0) {
        return (
            <li
                className={styles.root}
                style={alignStyle}
                onClick={delegateClickToLink}
            >
                <MediaChooserBlock
                    value={media}
                    wrapperAttrs={{
                        className: hasItems
                            ? styles.mediaWithOverlay
                            : styles.media,
                    }}
                    imageProps={{ sizes: categoryStyles.gridCardSizes }}
                />
                {hasItems && (
                    <Themed theme="darkThemeText">
                        <AdaptiveContent items={items} />
                    </Themed>
                )}
            </li>
        );
    }

    if (!hasItems) return null;

    return (
        <Themed
            tag="li"
            theme={content.theme}
            className={styles.root}
            style={alignStyle}
            onClick={delegateClickToLink}
        >
            <AdaptiveContent items={items} />
        </Themed>
    );
};
